import type { DeliveryAirport } from "@/lib/api";

/**
 * Task #4794 — single source of truth for matching a selected delivery
 * location to a host's configured flat-fee airport.
 *
 * The host stores a flat delivery fee per airport keyed by IATA code in
 * `car_delivery_airports`. When a renter/host picks a location from the
 * `/address/search` autocomplete, a canonical airport result carries an
 * IATA code (`airportCodeHint`). We match the host's configured fee by
 * exact code first (Task #4674); only fall back to fuzzy name/token
 * matching for codeless geocoder results (cities/streets).
 *
 * This logic used to be duplicated across CheckoutLocationEditor and
 * BookingWidget and drifted apart — the booking widget's copy dropped the
 * code and relied on fuzzy name matching, so a host airport whose stored
 * name didn't word-for-word match the search display name missed and the
 * quote fell through to per-mile distance pricing. Keep this the only
 * implementation so the surfaces can't diverge again.
 */
export function matchDeliveryAirport(
  deliveryAirports: DeliveryAirport[],
  displayName: string,
  airportCodeHint?: string | null,
): DeliveryAirport | undefined {
  const addrNorm = displayName.toLowerCase();
  const addrTokens = addrNorm.split(/[\s,]+/).filter(Boolean);
  const codeHintNorm = (airportCodeHint || "").toLowerCase();
  return deliveryAirports.find((ap) => {
    const nameNorm = ap.airport_name.toLowerCase();
    const codeNorm = ap.airport_code.toLowerCase();
    // When the picked suggestion carries an IATA code, match by exact
    // code only — a codeless fuzzy fallback would mis-attribute the fee.
    if (codeHintNorm && codeNorm === codeHintNorm) return true;
    if (codeHintNorm) return false;
    // Codeless geocoder result: fall back to fuzzy name/token matching.
    if (addrTokens.includes(codeNorm)) return true;
    if (addrNorm.includes(nameNorm)) return true;
    const nameWords = nameNorm.split(/\s+/);
    if (nameWords.length >= 2 && nameWords.every((w) => addrNorm.includes(w)))
      return true;
    return false;
  });
}
